import "./conditional-details/conditional-details";
import "./custom-details/custom-details";
import "./klaviyo/klaviyo-form";
import "./video/video";
import "./modal/modal";
import "./tab/tab";
import "../helpers/cart/cart.jsx";
import "./accordion/accordion";
import "./trap-focus/trap-focus.js";
import "./full-screen-dimmer/full-screen-dimmer";
import "./lock-scroll/lock-scroll";
import "./animate-in-view/animate-in-view";
import "./animate-scroll/animate-scroll";
import "./confirmCartRestored/confirmCartRestored";
import "./aria-listbox-v2/aria-listbox-v2";
import "./aria-listbox-v2/aria-combobox";
import "./aria-listbox-v2/aria-combobox-trigger";
import "./load-in-view/load-in-view";
import "./apps/engelai-dragger";
import { register } from "swiper/element/bundle";
import Swiper from "swiper";
import {
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
  Scrollbar,
  FreeMode,
  Mousewheel,
  Thumbs,
} from "swiper/modules";

// Load Swiper once for re-use by any other components
if (!window.swiperLoaded) {
  window.loadSwiperFulfillPromise({
    Swiper,
    Autoplay,
    EffectFade,
    Navigation,
    Pagination,
    Scrollbar,
    FreeMode,
    Mousewheel,
    Thumbs,
  });
  window.swiperLoaded = true;
}

// Register swiper element
register();

/**
 * Sets Document Hight Variable for mobile devices
 */
const footer = document.querySelector(".footer");
const documentHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty("--window-height", `${window.screen.height}px`);
  if (footer) {
    doc.style.setProperty(
      "--footer-height",
      `${footer.getBoundingClientRect().height}px`,
    );
  }
};
window.addEventListener("resize", documentHeight);
documentHeight();

/**
 * Manages smooth scroll of all anchor links
 */
document.addEventListener("click", (e) => {
  if (
    !e.target.matches(
      'a[href^="#"]:not(a[href="#recover"], [data-ignore-smooth])',
    )
  )
    return;

  e.preventDefault();

  const c = e.target.getAttribute("href").substring(1);
  const el = document.getElementById(c);

  if (!el) return;

  window.scrollTo({
    top: el.offsetTop - 60,
    behavior: "smooth",
  });
});

/**
 * Scrapes a section element for data needing to be handled in section JS
 *
 * @param {DOMNode} container
 * @returns parsed JSON
 */
window.scrapeSectionData = (container) => {
  const dataEl = container.querySelector("[data-section-data]");
  if (!dataEl) return {};

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    console.warn(
      `Error parsing data for ${container.dataset.sectionType} ${container.dataset.sectionId}`,
    );
    return {};
  }
};

/**
 * Fetchs page data from alternate templates
 *
 * @param {String} path
 * @param {String} templateSuffix
 * @returns parsed JSON
 */
window.fetchPageData = async ({ path, options = {} }) => {
  if (!path || options === {}) return {};

  const { templateSuffix, sectionId, variantId } = options;
  const queryArray = [];

  if (templateSuffix) queryArray.push(`view=${templateSuffix}`);
  if (sectionId) queryArray.push(`section_id=${sectionId}`);
  if (variantId) queryArray.push(`variant=${variantId}`);

  const url = `${path}?${queryArray.join("&")}`;
  let html = {};

  await fetch(url)
    .then((response) => response.text())
    .then((response) => {
      const elId = sectionId ? `shopify-section-${sectionId}` : "MainContent";
      html = new DOMParser().parseFromString(response, "text/html");
      html = html.getElementById(elId).innerHTML;
    })
    .catch(() => {
      console.warn(`Error fetching data from ${url}`);
    });

  return html;
};

window.getSectionInnerHTML = (html, selector) => {
  return new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selector)?.innerHTML;
};

window.getSection = (html, selector) => {
  return new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selector);
};

/**
 * Base-64 encodes an ID
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns encoded ID
 */
window.encodeId = (id, type) => {
  return window.btoa(`gid://shopify/${type}/${id}`);
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns decoded ID
 */
window.decodeId = (id, type) => {
  return decodeURIComponent(escape(window.atob(id))).split(
    `gid://shopify/${type}/`,
  )[1];
};

/**
 * Formats a given integer to a standard fraction.
 * Assumes that input value is in USD
 *
 * @param {Integer} num A price, with cents
 * @param {Integer} fraction
 */
window.formatMoney = (num) => {
  num = num * window.theme.priceFactor;
  let price = window?.Shopify?.currency
    ? num * window?.Shopify?.currency.rate
    : num;
  let currency = window?.Shopify?.currency
    ? window?.Shopify?.currency?.active
    : "USD";

  // Round up non-USD prices to match Liquid/Shopify Markets behavior
  if (currency !== "USD") {
    price = Math.ceil(price);
  }

  return new Intl.NumberFormat("en-EN", {
    style: "currency",
    currency,
  })
    .format(price)
    .replace(/.00$/, "");
};

/**
 * Formats a given integer to a standard fraction.
 * Assumes input value is in display currency
 *
 * @param {Integer} num A price, with cents
 * @param {Integer} fraction
 */
window.formatMoneyWithoutConversion = (price) => {
  price = price * window.theme.priceFactor;
  let currency = window?.Shopify?.currency
    ? window?.Shopify?.currency?.active
    : "USD";
  return new Intl.NumberFormat("en-EN", {
    style: "currency",
    currency,
  })
    .format(price)
    .replace(/.00$/, "");
};

window.theme = window.theme || {};
theme.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || theme.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

window.serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    if (key.indexOf("[") >= 0) {
      const mainKey = key.split("[")[0];
      const subKey = key.split("[")[1].split("]")[0];
      obj[mainKey] = {};
      obj[mainKey][subKey] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }
  return JSON.stringify(obj);
};

window.fetchConfig = (type = "json") => {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
};

/**
 * Handleize string
 *
 * @param {string} `products array Object`
 * @returns handleized string object
 *
 */
window.handleize = (string) => {
  string = string.toLowerCase();

  const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];

  // For the old browsers
  for (let i = 0; i < toReplace.length; ++i) {
    string = string.replace(toReplace[i], "");
  }

  string = string.replace(/\W+/g, "-");

  if (string.charAt(string.length - 1) == "-") {
    string = string.replace(/-+\z/, "");
  }

  if (string.charAt(0) == "-") {
    string = string.replace(/\A-+/, "");
  }

  return string;
};

// Resize Shopify Image URL
window.getSizedImageUrl = (src, size) => {
  if (src) {
    const keys = ['width', 'height'];
    const params = size
      .split('x')
      .map((value, i) => ({
        key: keys[i],
        value,
      }))
      .filter(param => param.value)
      .map(({ key, value }) => `${key}=${value}`)
      .join('&');

    return `${src}${src.includes('?') ? '&' : '?'}${params}`;
  } else {
    return "";
  }
};

let latestCartRequestId = 1;

/**
 * Remove an item from the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} callback object of callbacks
 */
window.updateCart = (updates, sections, callback = {}, options = {}) => {
  if (callback?.onBeforeAdd) {
    callback.onBeforeAdd();
  }

  latestCartRequestId++;
  const requestId = latestCartRequestId;

  fetch(`${window.routes.cart_update_url}.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ updates: updates, sections: sections }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422 && response.message === "Cart Error") {
        if (callback?.onError) {
          callback.onError(response);
        }
      }

      if (requestId !== latestCartRequestId) return;

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          type: "lineItem",
          options,
        },
      });
      document.dispatchEvent(updateCart);

      if (callback?.onAfterAdd) {
        callback.onAfterAdd(response);
      }
    })
    .catch(console.error);
};

/**
 * Add an item to cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} callback object of callbacks
 */
window.addToCart = (items, callback = {}, options = {}) => {
  if (callback?.onBeforeAdd) {
    callback.onBeforeAdd();
  }
  if (!Array.isArray(items)) {
    items = [items];
  }
  const data = {
    items: [...items],
  };
  data.sections = "main-cart,header";

  fetch(`${window.routes.cart_add_url}.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422 && response.message === "Cart Error") {
        if (callback?.onError) {
          callback.onError(response);
        }
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          options,
        },
      });
      document.dispatchEvent(updateCart);

      if (callback?.onAfterAdd) {
        callback.onAfterAdd(response);
      }
    })
    .catch(console.error);
};

window.handleConfigPricing = (v, form) => {
  const bundlePricing = [];
  form
    .querySelectorAll("[data-bundle-configuration-price]")
    ?.forEach((price) => {
      const quantity = isFinite(parseInt(price.dataset.quantity))
        ? parseInt(price.dataset.quantity)
        : 1;
      bundlePricing.push(parseFloat(price.value) * quantity);
    });

  const productPrice = form.currentVariant?.priceV2?.amount
    ? form.currentVariant?.priceV2?.amount
    : form.currentVariant?.price / 100;

  const bundlePrice = bundlePricing.length
    ? bundlePricing.reduce((a, b) => a + b, 0)
    : 0;
  let bp = parseFloat(bundlePrice) + parseFloat(productPrice);
  const productComparePrice = form.currentVariant?.compareAtPriceV2?.amount;
  let currentVariantPrice = `<span class="price__regular " >${window.formatMoney(
    bp,
  )}</span>`;
  // currentVariantPrice += productComparePrice
  //   ? `<del class="price__compare">${window.formatMoney(
  //     productComparePrice
  //   )}</del>`
  //   : "";
  form.querySelectorAll("[data-price]").forEach((price) => {
    price.innerHTML = currentVariantPrice;
  });
  form.querySelectorAll("item-count").forEach((el) => {
    el.setAttribute("count", bundlePricing.length + 1);
  });
};

//indicators for Customizer
if (
  Shopify.designMode &&
  document.querySelector("product-landing-navigation,section-jumplinks")
) {
  document.querySelectorAll(".shopify-section").forEach((item, index) => {
    if (!item.closest("#MainContent")) return;

    const counter = `<span class="jumplink__counter text text-large">${index + 1
      }</span>`;
    const { position } = window.getComputedStyle(item);
    if (position === "static") {
      item.style.position = "relative";
    }
    item.insertAdjacentHTML("beforeend", counter);
  });
}

const compareModal = document.querySelector('[data-id="compare-modal"]');

if (compareModal) {
  const compareUrl = new URL(window.location);
  if (compareUrl.searchParams.has("help-me-choose")) {
    compareModal.toggleModal(open);
  }
}

const BREADCRUMB_KEY = "veer-breadcrumbs";

const saveBreadcrumb = () => {
  const oldData = JSON.parse(
    window.sessionStorage.getItem(BREADCRUMB_KEY) || "{}",
  );

  try {
    const pageData = {
      current: JSON.parse(document.querySelector("[data-page-data]").innerHTML),
      previous: oldData.current,
    };

    window.sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(pageData));
  } catch (error) {
    console.warn("unable to save breadcrumb", error);
  }
};

saveBreadcrumb();

class DynamicBreadcrumb extends HTMLElement {
  constructor() {
    super();

    try {
      const pageData = window.sessionStorage.getItem(BREADCRUMB_KEY);
      if (pageData) {
        const { previous } = JSON.parse(pageData);
        if (!previous) return;

        const { path, page_type, collection_title } = previous;
        if (page_type !== this.type) return;

        const a = this.querySelector("a");
        a.href = path;

        if (a.innerHTML.trim() === "") {
          a.innerHTML = collection_title;
        }

        this.breadcrumbParent.style.display = null;
      }
    } catch (error) {
      this.breadcrumbParent.style.display = "none";
      console.warn("unable to read breadcrumb data", error);
    }
  }

  get breadcrumbParent() {
    return this.closest("[data-breadcrumb-parent]");
  }

  get type() {
    return this.getAttribute("type");
  }
}

if (!customElements.get("dynamic-breadcrumb")) {
  customElements.define("dynamic-breadcrumb", DynamicBreadcrumb);
}

window.hideItemWithAnimation = (el) => {
  return new Promise((resolve) => {
    el.style.height = `${el.getBoundingClientRect().height}px`;
    el.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      el.style.transition = "height 0.2s ease-in-out 0.1s, opacity 0.3s ease-in-out";
      // el.style.transition = "opacity 0.45s ease";

      const handleTransitionEnd = (e) => {
        if (e.target !== el) return;

        el.removeEventListener("transitionend", handleTransitionEnd);
        el.remove();
        resolve();
      };

      el.addEventListener("transitionend", handleTransitionEnd);

      window.requestAnimationFrame(() => {
        el.style.height = 0;
        el.style.opacity = 0;
      });
    });
  });
};

window.showItemWithAnimation = (el) => {
  return new Promise((resolve) => {
    el.style.height = 0;
    el.style.opacity = 0;
    el.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      el.style.transition = "height 0.3s ease-in-out, opacity 0.3s ease-in-out";
      // el.style.transition = "opacity 0.45s ease";

      const handleTransitionEnd = (e) => {
        if (e.target !== el) return;
        console.log;
        el.removeEventListener("transitionend", handleTransitionEnd);

        el.style.height = null;
        el.style.overflow = null;
        resolve();
      };

      el.addEventListener("transitionend", handleTransitionEnd);

      window.requestAnimationFrame(() => {
        el.style.height = `${el.scrollHeight}px`;
        el.style.opacity = null;
      });
    });
  });
};

window.addEventListener('klaviyoForms', (e) => {
  console.log('klaviyoForms', e);
  if (e.detail.type === 'open') {
    const style = document.createElement('style');
    style.innerText = `@keyframes klaviyo-slideinup { from { transform: translate3d(0, 0, 0); visibility: visible; } to { transform: translate3d(0, 0, 0); } } @keyframes klaviyo-fadeinup {     from {         opacity: 0;         transform: translate3d(0, 0, 0);         visibility: visible     }      to {         opacity: 1;         transform: translate3d(0, 0, 0)     } }`;
    console.log('appending', style);
    document.body.append(style);

    setTimeout(() => {
      document.body.classList.add('klaviyo-form-opened');
    }, 360);
  }
});

(() => {
  const focusFirstElement = () => {
    const firstEl = document
      .querySelector("#MainContent")
      .querySelector("a,button,input");

    if (firstEl) {
      firstEl.focus();
    }
  };

  const url = new URL(window.location);

  if (url.hash === "#main") {
    focusFirstElement();
  }

  document.addEventListener("click", (e) => {
    const skipLink = e.target.closest("[data-skip-link]");
    if (skipLink) {
      e.preventDefault();
      focusFirstElement();
    }
  });
})();
